/**
 * Index collection blocks use two parallel variant namespaces:
 *
 * - section.variant (list | grid | split): page layout chosen in the section toolbar.
 * - props.indexVariantProps[section.variant]: persisted per-layout card styling (gap, padding, etc.).
 * - indexCollectionLayoutKey (runtime only): active section variant during hydration/render.
 * - props.variant (always "grid"): collection renderer key; variantProps.grid holds merged layout props
 *   built in hydrateIndexCollectionBlocks — not the section layout selector.
 *
 * Unlike freeform collection blocks, do not switch props.variant to change index list layout.
 */

import type { CollectionItem } from '@/lib/blocks/collection/blocks';
import {
  getIndexCollectionDefinitionByContentType,
  INDEX_COLLECTION_BLOCK_TYPE,
  type IndexCollectionContentType,
  type IndexCollectionDefinition,
} from '@/lib/blocks/collection/index-collection/registry';
import { readCollectionItems } from '@/lib/blocks/collection/item-utils';
import type { LayoutVariantEntry } from '@/lib/blocks/collection/variant-registry';
import { readRecord, readString } from '@/lib/prop-utils';
import type { PageBlock } from '@/types/block';

export { INDEX_COLLECTION_BLOCK_TYPE, type IndexCollectionContentType };

export interface IndexCollectionProps {
  variant: string;
  indexCollectionLayoutKey?: string;
  variantProps: Record<string, Record<string, unknown>>;
  indexVariantProps: Record<string, Record<string, unknown>>;
  items: CollectionItem[];
}

export type HydratedIndexCollectionBlock = PageBlock & {
  indexCollectionLayoutKey?: string;
};

export const INDEX_COLLECTION_VARIANT_PROP_SCOPE_KEYS = ['grid', 'list', 'split'] as const;
export const INDEX_COLLECTION_SHARED_BACKGROUND_KEYS = ['borderColor', 'backgroundColor'] as const;

export const INDEX_COLLECTION_VARIANT_PROPS_PERSISTENCE = {
  storageKey: 'indexVariantProps',
  sharedKeys: INDEX_COLLECTION_SHARED_BACKGROUND_KEYS,
  sharedScopeKeys: INDEX_COLLECTION_VARIANT_PROP_SCOPE_KEYS,
} as const;

export const INDEX_COLLECTION_VARIANTS: LayoutVariantEntry[] = [
  {
    key: 'grid',
    label: 'Cards',
    renderer: 'grid',
    defaultProps: {
      gap: 'md',
      padding: 'md',
      align: 'start',
    },
  },
];

export function getIndexCollectionVariants(): LayoutVariantEntry[] {
  return INDEX_COLLECTION_VARIANTS;
}

export function readIndexCollectionLayoutKey(
  block: Pick<PageBlock, 'props'> & { indexCollectionLayoutKey?: string },
): string | undefined {
  return readString(block.indexCollectionLayoutKey);
}

export function resolveIndexCollectionProps(
  block: HydratedIndexCollectionBlock,
): IndexCollectionProps {
  const props = block.props ?? {};
  const variant = readString(props.variant);
  const activeVariant = INDEX_COLLECTION_VARIANTS.find((entry) => entry.key === variant)?.key;
  if (!activeVariant) {
    throw new Error(`Unsupported index collection renderer variant: ${variant ?? 'missing'}`);
  }
  const variantProps =
    (readRecord(props.variantProps) as Record<string, Record<string, unknown>> | null) ?? {};
  const rawIndexVariantProps =
    (readRecord(props.indexVariantProps) as Record<string, Record<string, unknown>> | null) ?? {};

  return {
    variant: activeVariant,
    indexCollectionLayoutKey: readIndexCollectionLayoutKey(block),
    variantProps,
    indexVariantProps: rawIndexVariantProps,
    items: readCollectionItems(props.items),
  };
}

export function getIndexCollectionVariantProps(props: IndexCollectionProps) {
  const entry = INDEX_COLLECTION_VARIANTS.find((variant) => variant.key === props.variant);
  if (!entry) {
    throw new Error(`Unsupported index collection renderer variant: ${props.variant}`);
  }

  const baseProps = props.variantProps[props.variant] ?? entry.defaultProps;
  const scopedProps = props.indexCollectionLayoutKey
    ? props.indexVariantProps[props.indexCollectionLayoutKey]
    : undefined;

  return scopedProps ? { ...baseProps, ...scopedProps } : baseProps;
}

export function indexCollectionColumns(
  variant: string,
  variants: { list: string; grid: string; split: string },
) {
  if (variant === variants.grid) {
    return { mobile: 1, tablet: 2, desktop: 3 };
  }

  if (variant === variants.list) {
    return { mobile: 1, tablet: 1, desktop: 1 };
  }

  if (variant === variants.split) {
    return { mobile: 1, tablet: 2, desktop: 2 };
  }

  throw new Error(`Unsupported index section variant: ${variant}`);
}

function normalizeIndexLayoutVariant(
  variant: string,
  variants: { list: string; grid: string; split: string },
) {
  if (variant === variants.grid || variant === variants.list || variant === variants.split) {
    return variant;
  }

  throw new Error(`Unsupported index section variant: ${variant}`);
}

export function hydrateIndexCollectionBlocks<T>({
  blocks,
  records,
  isPreview,
  variant,
  contentType,
  variants,
  toCollectionItem,
  extraVariantProps,
}: {
  blocks: PageBlock[];
  records: T[];
  isPreview: boolean;
  variant: string;
  contentType: IndexCollectionContentType;
  variants: { list: string; grid: string; split: string };
  toCollectionItem: (record: T, index: number, isPreview: boolean) => CollectionItem;
  extraVariantProps?: (variant: string) => Record<string, unknown>;
}) {
  const items = records.map((record, index) => toCollectionItem(record, index, isPreview));

  return blocks.map((block) => {
    if (!isIndexCollectionBlock(block)) return block;
    const variantProps =
      (readRecord(block.props?.variantProps) as Record<string, Record<string, unknown>>) ?? {};
    const indexCollectionProps = resolveIndexCollectionProps(block);
    const normalizedVariant = normalizeIndexLayoutVariant(variant, variants);
    const activeVariantProps = variantProps.grid ?? {};
    const scopedVariantProps =
      indexCollectionProps.indexVariantProps[normalizedVariant] ??
      indexCollectionProps.indexVariantProps[variant] ??
      {};
    const computedVariantProps = {
      columns: indexCollectionColumns(normalizedVariant, variants),
      ...(extraVariantProps?.(normalizedVariant) ?? {}),
    };

    return {
      ...block,
      indexCollectionLayoutKey: normalizedVariant,
      props: {
        ...block.props,
        variant: 'grid',
        variantProps: {
          ...variantProps,
          grid: {
            ...activeVariantProps,
            ...scopedVariantProps,
            ...computedVariantProps,
          },
        },
        indexContentType: contentType,
        items,
      },
    } satisfies HydratedIndexCollectionBlock;
  });
}

export function isIndexCollectionBlock(block: Pick<PageBlock, 'blockType'>): boolean {
  return block.blockType === INDEX_COLLECTION_BLOCK_TYPE;
}

export function readIndexCollectionContentType(
  block: Pick<PageBlock, 'blockType' | 'props'>,
): IndexCollectionContentType | undefined {
  const raw = readString(block.props?.indexContentType);
  return getIndexCollectionDefinitionByContentType(raw)?.contentType;
}

export function readIndexCollectionDefinition(
  block: Pick<PageBlock, 'blockType' | 'props'>,
): IndexCollectionDefinition | undefined {
  const raw = readString(block.props?.indexContentType);
  return getIndexCollectionDefinitionByContentType(raw);
}
